import { useState } from 'react'
import cn from 'classnames'
import { Checkbox } from '@mc/design-system'
import { Trans, useTranslation } from '@mc/i18n'
import { useGrowthSubcategoriesByCategorySlug } from 'libs/client/api/growth'
import {
  ButtonsSection,
  InteractiveStep,
  Loader,
} from 'pages/find-my-classes/components/Quiz/components'
import { QuizMachineContext } from 'pages/find-my-classes/components/Quiz/context'
import { useQuizEvents } from 'pages/find-my-classes/components/Quiz/events'
import { useOptionalCategory } from 'pages/find-my-classes/hooks/useOptionalCategory'
import { QuizCategoryName } from './QuizCategoryName'
import { useSubcatInterestsEventData } from './useSubcatInterestsEventData'

export const SubcatInterests = () => {
  const { t } = useTranslation('@mc/next')
  const actor = QuizMachineContext.useActorRef()
  const { trackOptionSelected, trackSubcatPersonalizationComplete } =
    useQuizEvents()

  const { category, subcatInterests } = QuizMachineContext.useSelector(
    ({ context }) => {
      const currentCatId = context.catsToRefineSubcats?.[0]
      const fullCat = context.allCategories.find((c) => c.id === currentCatId)
      return {
        category: fullCat,
        subcatInterests: context.subcatInterests,
      }
    },
  )

  const { isOptionalCategory } = useOptionalCategory(category)

  const subStepNumber = QuizMachineContext.useSelector(({ context }) => {
    const catIDs = (context.categoryInterestsCSV || '').split(',')
    const catsUnrefined = context.catsToRefineSubcats || []
    return catIDs.length - catsUnrefined.length + 1
  })

  const { subcats } = useGrowthSubcategoriesByCategorySlug(category?.slug ?? '')
  const [selectedSubcatIds, setSelectedSubcatIds] = useState(
    new Set<number>(subcatInterests),
  )

  const eventData = useSubcatInterestsEventData([...selectedSubcatIds])

  if (!category) {
    actor.send({ type: 'back' })
    return null
  }
  if (!subcats) {
    return <Loader />
  }

  const subcatIdsWithinCat = Array.from(selectedSubcatIds).filter(
    (subcatId) => subcats.findIndex((sc) => sc.id === subcatId) >= 0,
  )

  return (
    <InteractiveStep>
      <h1
        className='mc-text-h4 mc-text--balance mc-text-color--medium'
        style={{ fontWeight: 'inherit' }}
      >
        <Trans
          i18nKey='findMyClasses.subcatInterests.title'
          ns={'@mc/next'}
          values={{ categoryName: category.name }}
          components={{
            bold: <QuizCategoryName />,
          }}
        />
      </h1>
      <div className='mc-my-7'>
        <div className='row no-gutters-vertical justify-content-center'>
          <div className='col-12 col-md-10 col-lg-7'>
            <div className='mc-my-2'>
              {isOptionalCategory && (
                <div className='mc-text--left mc-mb-2 mc-text-color--neutral-500'>
                  {t('findMyClasses.subcatInterests.optional')}
                </div>
              )}
              {subcats.map((subcat, i) => (
                <Checkbox
                  key={subcat.id}
                  className={cn(
                    'mc-p-6 mc-my-1',
                    'mc-text--left',
                    'mc-bg-gray-100',
                    {
                      'mc-bg-gray-300': selectedSubcatIds.has(subcat.id),
                      'mc-corners-top-left--sm mc-corners-top-right--sm':
                        i === 0,
                      'mc-corners-bottom-left--sm mc-corners-bottom-right--sm':
                        i === subcats.length - 1,
                    },
                  )}
                  checked={selectedSubcatIds.has(subcat.id)}
                  label={subcat.name}
                  option={subcat.id}
                  onChange={() =>
                    setSelectedSubcatIds((curr) => {
                      const nextSel = new Set(curr)
                      const checked = !curr.has(subcat.id)
                      if (checked) nextSel.add(subcat.id)
                      else nextSel.delete(subcat.id)
                      trackOptionSelected({
                        value: subcat.name,
                        checked,
                        subStepNumber,
                      })
                      return nextSel
                    })
                  }
                  name={`option-${subcat.id}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <ButtonsSection
        key={category.name}
        disabled={!isOptionalCategory && subcatIdsWithinCat.length === 0}
        disabledText={t('findMyClasses.subcatInterests.noneSelected')}
        onNext={() => {
          trackSubcatPersonalizationComplete(eventData)
        }}
        machineEvent={{
          machineEventType: 'subcat_refinement' as const,
          categoryIdRefined: category.id,
          subcatInterests: subcatIdsWithinCat,
        }}
      />
    </InteractiveStep>
  )
}
